import { SearchDataCatalogInput, SearchDataCatalogPath } from "~/gen/api/governance"
import { TagCriteria, AtCriteria, FieldFuzzyCriteria, PathCriteria, ClassificationCriteria, EncryptStatusCriteria, CriteriaType, Criteria } from "./types"


const tagCriteriaReducer = (acc: SearchDataCatalogInput, criteria: TagCriteria) => {
  const tags = acc.tags || (acc.tags = [])
  if (tags.indexOf(criteria.id) === -1) {
    tags.push(criteria.id)
  }
  return acc
}

const atCriteriaReducer = (acc: SearchDataCatalogInput, criteria: AtCriteria) => {
  const fuzzyTableNames = acc.fuzzyTableNames || (acc.fuzzyTableNames = [])
  const s = criteria.at.trim()
  if (fuzzyTableNames.indexOf(s) === -1) {
    fuzzyTableNames.push(s)
  }  
  return acc
}

const fieldFuzzyCriteriaReducer = (acc: SearchDataCatalogInput, criteria: FieldFuzzyCriteria) => {
  const fuzzyFieldNames = acc.fuzzyFieldNames || (acc.fuzzyFieldNames = [])
  for(var field of criteria.fields.map(s => s.trim())) {
    if (fuzzyFieldNames.indexOf(field) === -1) {
      fuzzyFieldNames.push(field)
    }  
  }
  return acc
}

const trimValue = (s:string) => s.replace(/^\**/, '').replace(/\**$/, '').trim()
const pathCriteriaReducer = (acc: SearchDataCatalogInput, criteria: PathCriteria, i:number, arr: PathCriteria[]) => {
  const paths = acc.searchPaths || (acc.searchPaths = [])
  const path: SearchDataCatalogPath = {}
  if (criteria.database) {
    if (criteria.database.usage === 'exact') {
      path.databaseId = criteria.database.id as number
    } else {
      path.fuzzyDatabaseName = trimValue(criteria.database.value || '') || '*'
    }
  }
  
  if (criteria.schema) {
    if (criteria.schema.usage === 'exact') {
      path.exactSchemaName = (criteria.schema.id as string) || criteria.schema.value || undefined
    } else {
      path.fuzzySchemaName = trimValue(criteria.schema.value || '') || '*'
    }
  }

  if (criteria.table) {
    if (criteria.table.usage === 'exact') {
      if (criteria.table.id) {
        path.tableId = criteria.table.id as number
      } else {
        path.exactTableName = criteria.table.value
      }
    } else {
      path.fuzzyTableName = trimValue(criteria.table.value || '') || '*'
    }
  }

  if (criteria.field) {
    if (criteria.field.usage === 'exact') {
      if (criteria.field.id) {
        path.fieldId = criteria.field.id as number
      } else {
        path.exactFieldName = criteria.field.value
      }
    } else {
      path.fuzzyFieldName = trimValue(criteria.field.value || '') || '*'
    }
  }

  if (!Object.values(path).every(x => x === '*')) {
    paths.push(path)
  }
  return acc
}

const classificationCriteriaReducer = (acc: SearchDataCatalogInput, criteria: ClassificationCriteria, i:number, arr: ClassificationCriteria[]) => {
  if (acc.primaryClassifications === undefined) {
    acc.primaryClassifications = 
      [...new Set(arr.filter(x => x.secondaryId === undefined).map(x => x.primaryId))]
      .map(x => x === undefined ? null : x) as number[]
  }
  const pids = acc.primaryClassifications as number[]
  const sids = acc.secondaryClassifications || (acc.secondaryClassifications = [])
  
  if (criteria.secondaryId !== undefined && pids.indexOf(criteria.primaryId as any) === -1) {
    sids.push(criteria.secondaryId as any)
  }
  return acc
}

const encryptStatusCriteriaReducer = (acc: SearchDataCatalogInput, criteria: EncryptStatusCriteria) => {
  const encryptMappingStatuses = acc.encryptMappingStatuses || (acc.encryptMappingStatuses = [])
  if (encryptMappingStatuses.indexOf(criteria.encryptStatus) === -1) {
    encryptMappingStatuses.push(criteria.encryptStatus)
  }  
  return acc
}

const reducerMap: Record<CriteriaType, any> = {
  'Tag': tagCriteriaReducer,
  'At': atCriteriaReducer,
  'FieldFuzzy': fieldFuzzyCriteriaReducer,
  'Path': pathCriteriaReducer,
  'Classification': classificationCriteriaReducer,
  'EncryptStatus': encryptStatusCriteriaReducer,
}

export const aggregateCriterias = (criterias: Criteria[]) => {
  const filters: SearchDataCatalogInput = {}
  const groups = criterias.reduce((acc, c) => {
    const arr = acc[c.kind] || (acc[c.kind] = [])
    arr.push(c)
    return acc
  }, {} as Record<CriteriaType, Criteria[]>)
  
  for(var key in groups) {
    const k: CriteriaType = key as any
    const reducer = reducerMap[k]
    groups[k].reduce(reducer, filters)
  }
  return filters
}
